import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useState,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';

import {
  registerUser,
  loginUser,
  loginWithSocial,
  resetPassword,
  onAuthStateChange,
} from '../lib/auth';
import { AuthErrorHandler } from '../lib/errorHandler';
import { LogoutManager } from '../lib/logoutManager';
import { tokenManager } from '../lib/tokenManager';
// Directus同期処理のインポートを削除（Supabase認証のみに移行）
// import { handleAuthStateChange } from '../lib/userSync';
// import {
//   validateUserSyncState,
//   autoRecoverUserSync,
//   getUserSyncErrorMessage,
//   getUserSyncDebugInfo,
//   UserSyncState,
// } from '../lib/userSyncValidation';
import { getUserFavorites, addToFavorites, removeFromFavorites } from '../services/directus';
import {
  User,
  AuthState,
  AuthError,
  AuthErrorType,
  LoginCredentials,
  RegisterCredentials,
  ResetPasswordData,
  SocialProvider,
} from '../types/auth';
import { DirectusFavorite as Favorite } from '../types/directus';

import { useToast } from './ToastContext';

// 認証アクションの型定義
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: User }
  | { type: 'AUTH_ERROR'; payload: AuthError }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_FAVORITES'; payload: Favorite[] }
  | { type: 'ADD_FAVORITE'; payload: Favorite }
  | { type: 'REMOVE_FAVORITE'; payload: string }; // 明示的に文字列として定義

// AuthContextの型定義
export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: (redirectTo?: string) => Promise<void>;
  logoutWithConfirm: (message?: string, redirectTo?: string) => Promise<boolean>;
  emergencyLogout: (reason?: string) => Promise<void>;
  loginWithProvider: (provider: SocialProvider) => Promise<void>;
  resetUserPassword: (data: ResetPasswordData) => Promise<void>;
  setUser: (user: User) => void;
  clearError: () => void;
  retryLastOperation: () => Promise<void>;
  // お気に入り機能
  favorites: Favorite[];
  isFavorited: (targetId: string, targetType: 'creature' | 'article' | 'dinosaur') => boolean;
  toggleFavorite: (
    targetId: string,
    targetType: 'creature' | 'article' | 'dinosaur'
  ) => Promise<boolean>;
  refreshFavorites: () => Promise<void>;
}

// 初期状態
const initialState: AuthState & { favorites: Favorite[] } = {
  user: null,
  isAuthenticated: false,
  isLoading: true, // 初期化時はローディング状態
  error: null,
  favorites: [],
};

// Reducer関数
const authReducer = (
  state: AuthState & { favorites: Favorite[] },
  action: AuthAction
): AuthState & { favorites: Favorite[] } => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: AuthErrorHandler.getDisplayMessage(action.payload),
        errorDetails: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
        errorDetails: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_FAVORITES':
      return {
        ...state,
        favorites: action.payload,
      };
    case 'ADD_FAVORITE':
      return {
        ...state,
        favorites: [...state.favorites, action.payload],
      };
    case 'REMOVE_FAVORITE':
      return {
        ...state,
        favorites: state.favorites.filter(fav => fav.id !== action.payload),
      };
    default:
      return state;
  }
};

// AuthContextの作成
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProviderコンポーネント
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [lastOperation, setLastOperation] = useState<(() => Promise<void>) | null>(null);
  const { showSuccess } = useToast();

  // ユーザーログイン処理（簡素化）
  const handleUserLogin = async (user: User, session: any) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.log('handleUserLogin開始:', { userId: user.id, email: user.email });
      }

      // ユーザー情報が無効な場合は処理をスキップ
      if (!user || !user.id) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.log('ユーザー情報が無効なため、処理をスキップします');
        }
        return;
      }

      // ユーザー認証成功
      dispatch({ type: 'AUTH_SUCCESS', payload: user });

      // ログイン成功時のToast通知を表示
      showSuccess('ログインしました');

      // お気に入り一覧を取得（Supabase IDで直接取得）
      try {
        const userFavorites = await getUserFavorites(user.id);
        dispatch({ type: 'SET_FAVORITES', payload: userFavorites as any });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('お気に入り一覧の取得に失敗:', error);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('ユーザーログイン処理中にエラーが発生:', error);
      dispatch({
        type: 'AUTH_ERROR',
        payload: {
          type: AuthErrorType.UNKNOWN_ERROR,
          message: 'ログイン処理中にエラーが発生しました',
        },
      });
    }
  };

  // ユーザーログアウト処理（簡素化）
  const handleUserLogout = async () => {
    dispatch({ type: 'AUTH_LOGOUT' });
    dispatch({ type: 'SET_FAVORITES', payload: [] });
    // ログアウト時に自動リフレッシュを停止
    tokenManager.stopAutoRefresh();
  };

  // 認証状態の変更を監視
  useEffect(() => {
    const authStateChangeResult = onAuthStateChange(async (event, session) => {
      if (session?.user) {
        // ユーザー変数をtryブロックの外で定義
        const user = mapSupabaseUserToUser(session.user);
        await handleUserLogin(user, session);
      } else {
        await handleUserLogout();
      }
    });

    // authStateChangeResult が undefined でないことを確認
    if (authStateChangeResult && authStateChangeResult.data) {
      const { subscription } = authStateChangeResult.data;

      return () => {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
        if (tokenManager) {
          tokenManager.stopAutoRefresh();
        }
      };
    }

    // authStateChangeResult が undefined の場合、クリーンアップ関数を返さない
    return () => {
      if (tokenManager) {
        tokenManager.stopAutoRefresh();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 依存配列を空にして、初回のみ実行されるようにする

  // ログイン関数
  const login = useCallback(async (credentials: LoginCredentials): Promise<void> => {
    const operation = async () => {
      dispatch({ type: 'AUTH_START' });
      const user = await loginUser(credentials);

      // ユーザーが正しく取得できたか確認
      if (!user || !user.id) {
        const error = new Error('ログインに失敗しました。アカウントが存在しない可能性があります。');
        const authError = AuthErrorHandler.handleAuthError(error, 'login context');
        dispatch({ type: 'AUTH_ERROR', payload: authError });
        // eslint-disable-next-line sonarjs/no-redundant-jump
        return;
      }

      // ログイン成功時の処理はonAuthStateChangeで自動実行されるため、
      // ここでは明示的にhandleUserLoginを呼ばない
    };

    setLastOperation(() => operation);

    try {
      await operation();
    } catch (error) {
      const authError = AuthErrorHandler.handleAuthError(error, 'login context');
      dispatch({ type: 'AUTH_ERROR', payload: authError });
      // エラーを再スローしないで、contextで処理済みとする
    }
  }, []);

  // 登録関数
  const register = useCallback(async (credentials: RegisterCredentials): Promise<void> => {
    const operation = async () => {
      dispatch({ type: 'AUTH_START' });
      const user = await registerUser(credentials);
      dispatch({ type: 'AUTH_SUCCESS', payload: user });
    };

    setLastOperation(() => operation);

    try {
      await operation();
    } catch (error) {
      // 既存ユーザーエラーの場合は、自動ログインを試行せずにエラーを投げる
      if (
        error &&
        typeof error === 'object' &&
        'type' in error &&
        error.type === AuthErrorType.USER_ALREADY_EXISTS
      ) {
        // 既存ユーザーエラーをそのまま投げる（RegisterFormで適切に処理される）
        throw error;
      }

      // エラーが既にAuthErrorの場合はそのまま使用
      let authError: AuthError;
      if (
        error &&
        typeof error === 'object' &&
        'type' in error &&
        'message' in error &&
        typeof (error as any).type === 'string' &&
        typeof (error as any).message === 'string'
      ) {
        authError = error as AuthError;
      } else {
        authError = AuthErrorHandler.handleAuthError(error, 'register context');
      }

      dispatch({ type: 'AUTH_ERROR', payload: authError });

      // 確認メール送信エラーの場合は例外を再スロー
      if (authError.type === AuthErrorType.EMAIL_SEND_FAILURE) {
        throw authError;
      }
      // その他のエラーはcontextで処理済みとする
    }
  }, []);

  // ログアウト関数
  const logout = useCallback(
    async (redirectTo?: string): Promise<void> => {
      try {
        dispatch({ type: 'AUTH_START' });

        // 自動リフレッシュを停止
        tokenManager.stopAutoRefresh();

        dispatch({ type: 'AUTH_LOGOUT' });

        // ログアウト成功時にトーストを表示（リダイレクト前に実行）
        showSuccess('ログアウトしました');

        // LogoutManagerを使用して完全ログアウト
        // デフォルトでTOPページにリダイレクト
        await LogoutManager.performCompleteLogout(redirectTo || '/', false);
      } catch (error) {
        const authError = AuthErrorHandler.handleAuthError(error, 'logout context');
        dispatch({ type: 'AUTH_ERROR', payload: authError });
        // エラーが発生してもログアウト状態にする
        dispatch({ type: 'AUTH_LOGOUT' });

        // エラーが発生した場合もTOPページにリダイレクト
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      } finally {
        // setIsLoggingOut(false); // この行を削除
      }
    },
    [showSuccess]
  );

  // 確認付きログアウト関数
  const logoutWithConfirm = useCallback(
    async (message?: string, redirectTo?: string): Promise<boolean> => {
      try {
        dispatch({ type: 'AUTH_START' });

        // 自動リフレッシュを停止
        tokenManager.stopAutoRefresh();

        const confirmed = await LogoutManager.confirmLogout(message, redirectTo || '/');

        if (confirmed) {
          dispatch({ type: 'AUTH_LOGOUT' });

          // ログアウト成功時にトーストを表示（リダイレクト前に実行）
          showSuccess('ログアウトしました');
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }

        return confirmed;
      } catch (error) {
        const authError = AuthErrorHandler.handleAuthError(error, 'logout with confirm context');
        dispatch({ type: 'AUTH_ERROR', payload: authError });

        // エラーが発生した場合もTOPページにリダイレクト
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
        return false;
      } finally {
        // setIsLoggingOut(false); // この行を削除
      }
    },
    [showSuccess]
  );

  // 緊急ログアウト関数
  const emergencyLogout = useCallback(
    async (reason?: string): Promise<void> => {
      try {
        dispatch({ type: 'AUTH_START' });
        // 自動リフレッシュを停止
        tokenManager.stopAutoRefresh();

        dispatch({ type: 'AUTH_LOGOUT' });

        // ログアウト成功時にトーストを表示（リダイレクト前に実行）
        showSuccess('ログアウトしました');

        // 緊急ログアウト実行
        await LogoutManager.emergencyLogout(reason);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Emergency logout failed:', error);
        // 最後の手段として状態だけリセット
        dispatch({ type: 'AUTH_LOGOUT' });

        // エラーが発生した場合もTOPページにリダイレクト
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      } finally {
        // setIsLoggingOut(false); // この行を削除
      }
    },
    [showSuccess]
  );

  // ソーシャルログイン関数
  const loginWithProvider = useCallback(async (provider: SocialProvider): Promise<void> => {
    try {
      if (process.env.NODE_ENV === 'development') {
      }
      dispatch({ type: 'AUTH_START' });

      await loginWithSocial(provider);
    } catch (error) {
      const authError = AuthErrorHandler.handleAuthError(error, 'social login context');
      dispatch({ type: 'AUTH_ERROR', payload: authError });
    }
  }, []);

  // パスワードリセット関数
  const resetUserPassword = useCallback(async (data: ResetPasswordData): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_START' });

      await resetPassword(data);
    } catch (error) {
      const authError = AuthErrorHandler.handleAuthError(error, 'password reset context');
      dispatch({ type: 'AUTH_ERROR', payload: authError });
    }
  }, []);

  // ユーザー情報を手動で設定
  const setUser = useCallback((user: User): void => {
    dispatch({ type: 'AUTH_SUCCESS', payload: user });
  }, []);

  // エラーをクリア
  const clearError = useCallback((): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  // 最後の操作を再試行
  const retryLastOperation = useCallback(async (): Promise<void> => {
    if (lastOperation) {
      try {
        await lastOperation();
      } catch (error) {
        const authError = AuthErrorHandler.handleAuthError(error, 'retry operation');
        dispatch({ type: 'AUTH_ERROR', payload: authError });
      }
    }
  }, [lastOperation]);

  // お気に入り一覧を取得
  const refreshFavorites = useCallback(async (): Promise<void> => {
    if (!state.user?.id) {
      dispatch({ type: 'SET_FAVORITES', payload: [] });
      return;
    }

    try {
      const userFavorites = await getUserFavorites(state.user.id);
      dispatch({ type: 'SET_FAVORITES', payload: userFavorites as any });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('お気に入り一覧の取得に失敗:', error);
    }
  }, [state.user?.id]);

  // お気に入り状態を確認
  const isFavorited = useCallback(
    (targetId: string, targetType: 'creature' | 'article' | 'dinosaur'): boolean => {
      return state.favorites.some(
        fav => fav.target_id === targetId && fav.target_type === targetType
      );
    },
    [state.favorites]
  );

  // お気に入りをトグル
  const toggleFavorite = useCallback(
    async (targetId: string, targetType: 'creature' | 'article' | 'dinosaur'): Promise<boolean> => {
      if (!state.user?.id) return false;

      try {
        const currentlyFavorited = state.favorites.some(
          fav => fav.target_id === targetId && fav.target_type === targetType
        );
        // eslint-disable-next-line no-console
        console.log('[toggleFavorite] 現在の状態:', { currentlyFavorited, targetId, targetType });
        // eslint-disable-next-line no-console
        console.log('[toggleFavorite] 現在のfavorites:', state.favorites);

        if (currentlyFavorited) {
          // お気に入りから削除
          const favorite = state.favorites.find(
            fav => fav.target_id === targetId && fav.target_type === targetType
          );
          // eslint-disable-next-line no-console
          console.log('[toggleFavorite] 削除対象のfavorite:', favorite);

          if (favorite) {
            const success = await removeFromFavorites(favorite.id, targetId, targetType);
            // eslint-disable-next-line no-console
            console.log('[toggleFavorite] 削除API結果:', success);

            if (success) {
              dispatch({ type: 'REMOVE_FAVORITE', payload: favorite.id });
              // eslint-disable-next-line no-console
              console.log('[toggleFavorite] ✓ お気に入りから削除しました');
              return true;
            }
          } else {
            // eslint-disable-next-line no-console
            console.error('[toggleFavorite] お気に入りが見つかりません');
          }
        } else {
          // お気に入りに追加
          const newFavorite = await addToFavorites(state.user.id, targetId, targetType);
          if (newFavorite) {
            dispatch({ type: 'ADD_FAVORITE', payload: newFavorite as any });
            return true;
          }
        }
        return false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('お気に入りの操作に失敗:', error);
        return false;
      }
    },
    [state.user?.id, state.favorites]
  );

  const value: AuthContextType = useMemo(
    () => ({
      ...state,
      login,
      register,
      logout,
      logoutWithConfirm,
      emergencyLogout,
      loginWithProvider,
      resetUserPassword,
      setUser,
      clearError,
      retryLastOperation,
      favorites: state.favorites,
      isFavorited,
      toggleFavorite,
      refreshFavorites,
    }),
    [
      state,
      login,
      register,
      logout,
      logoutWithConfirm,
      emergencyLogout,
      loginWithProvider,
      resetUserPassword,
      setUser,
      clearError,
      retryLastOperation,
      isFavorited,
      toggleFavorite,
      refreshFavorites,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// カスタムフック
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ヘルパー関数
const mapSupabaseUserToUser = (supabaseUser: any): User => ({
  id: supabaseUser.id,
  email: supabaseUser.email || '',
  username: supabaseUser.user_metadata?.username,
  avatar_url: supabaseUser.user_metadata?.avatar_url,
  email_confirmed_at: supabaseUser.email_confirmed_at,
});
